/* ============================================================
   ui.ts — everything the player SEES outside the falling action:
   screen switching, the HUD, popups, level select, post-game
   stats, the "Learn About Waste" page, and the local leaderboard.
   ============================================================ */

import { BINS, ITEMS_BY_BIN, LEVELS, QUOTES } from "./data";
import type { HudData, LevelEndData, WrongDropData } from "./game";
import type { Level } from "./data";

const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el;
};

const LB_KEY = "sortTheTrash.leaderboard";
const SOUND_KEY = "sortTheTrash.sound";
const UNLOCK_KEY = "sortTheTrash.unlocked";

interface ScoreEntry {
  score: number;
  date: string;
}

/* ---------- Screen switching ---------- */
export function showScreen(id: string): void {
  document.querySelectorAll(".screen").forEach((s) => s.classList.remove("active"));
  $(id).classList.add("active");
}

/* ---------- Splash loading animation ---------- */
export function runSplash(onDone: () => void): void {
  $("splash-quote").textContent = "“" + QUOTES[Math.floor(Math.random() * QUOTES.length)] + "”";
  const fill = $("loading-fill");
  let pct = 0;
  const tick = setInterval(() => {
    pct += 8 + Math.random() * 12;
    if (pct >= 100) {
      pct = 100;
      clearInterval(tick);
      $("loading-text").textContent = "Ready!";
      setTimeout(onDone, 350);
    }
    fill.style.width = pct + "%";
  }, 120);
}

/* ---------- HUD ---------- */
export function updateHud({ score, lives, level, progress }: HudData): void {
  $("hud-score").textContent = score.toLocaleString();
  $("hud-lives").textContent = "❤️".repeat(Math.max(0, lives)) || "💔";
  $("hud-level").textContent = "Level " + level;
  $("hud-progress").style.width = Math.round(progress * 100) + "%";
}

/* ---------- Level intro banner ("Level 3 — Introducing Compost") ---------- */
let introTimer: ReturnType<typeof setTimeout> | undefined;
export function showLevelIntro(level: Level, done: () => void): void {
  $("level-intro-num").textContent = "Level " + level.level;
  $("level-intro-focus").textContent = level.focus;
  const el = $("level-intro");
  el.hidden = false;
  clearTimeout(introTimer);
  introTimer = setTimeout(() => {
    el.hidden = true;
    done();
  }, 1600);
}

/* ---------- Wrong-bin popup ---------- */
export function showWrong({ item, correctBin }: WrongDropData, continueFn: () => void): void {
  $("wrong-emoji").textContent = item.emoji;
  $("wrong-item").textContent = item.name;
  $("wrong-fact").textContent = item.fact;
  $("wrong-correct").textContent = `It belongs in: ${correctBin.emoji} ${correctBin.label}`;
  const modal = $("modal-wrong");
  modal.hidden = false;

  const btn = $("btn-wrong-continue");
  const handler = (): void => {
    btn.removeEventListener("click", handler);
    modal.hidden = true;
    continueFn();
  };
  btn.addEventListener("click", handler);
}

/* ---------- Level unlock progress (saved on this device) ---------- */
function getUnlocked(): number {
  const n = Number(localStorage.getItem(UNLOCK_KEY));
  return Number.isFinite(n) && n >= 1 ? Math.min(n, LEVELS.length) : 1;
}

function unlockUpTo(levelNumber: number): void {
  const n = Math.min(levelNumber, LEVELS.length);
  if (n > getUnlocked()) localStorage.setItem(UNLOCK_KEY, String(n));
}

/* ---------- Level select grid ---------- */
export function renderLevels(onPick: (levelIndex: number) => void): void {
  const unlocked = getUnlocked();
  const grid = $("level-grid");
  grid.innerHTML = "";
  LEVELS.forEach((lv, i) => {
    const locked = lv.level > unlocked;
    const b = document.createElement("button");
    b.className = "level-btn" + (locked ? " locked" : "");
    b.disabled = locked;
    b.innerHTML = locked
      ? `<span class="level-num">🔒</span><span class="level-focus">Level ${lv.level}</span>`
      : `<span class="level-num">${lv.level}</span><span class="level-focus">${lv.focus}</span>`;
    if (!locked) b.addEventListener("click", () => onPick(i));
    grid.appendChild(b);
  });
}

/* ---------- Post-game stats ---------- */
export function showLevelEnd({ won, isLastLevel, stats }: LevelEndData): void {
  if (won) unlockUpTo(stats.level + 1);

  // The run is over when you win the final level or run out of lives —
  // that's when the score lands on the leaderboard.
  const runOver = (won && isLastLevel) || !won;
  if (runOver) saveScore(stats.score);

  const finalWin = won && isLastLevel;
  let title: string;
  if (finalWin) title = "🎉 You Win!";
  else if (won) title = "✅ Level " + stats.level + " Complete!";
  else title = "💔 Out of Lives";

  // Sustainability rating from accuracy
  const correct = finalWin ? stats.runCorrect : stats.correct;
  const mistakes = finalWin ? stats.runMistakes : stats.mistakes;
  const total = correct + mistakes;
  const acc = total ? correct / total : 0;
  let rating: string;
  if (!won) rating = "🌱 Keep Trying!";
  else if (stats.perfect) rating = "🌍 Sustainability Hero";
  else if (acc >= 0.8) rating = "♻️ Eco Champion";
  else rating = "🌱 Eco Learner";

  $("stats-title").textContent = title;
  $("stats-rating").textContent = rating;
  $("stat-score").textContent = stats.score.toLocaleString();
  // The final-win screen shows totals for the whole run
  $("stat-correct").textContent = String(correct);
  $("stat-mistakes").textContent = String(mistakes);
  $("stat-best-streak").textContent = String(finalWin ? stats.runBestStreak : stats.bestStreak);

  $("btn-next").hidden = !(won && !isLastLevel);
  showScreen("screen-stats");
  if (won) confetti($("screen-stats"));
}

/* ---------- Confetti for wins ---------- */
function confetti(container: HTMLElement): void {
  const EMO = ["🎉", "♻️", "🌱", "⭐", "🍃"];
  for (let i = 0; i < 36; i++) {
    const s = document.createElement("span");
    s.className = "confetti-piece";
    s.textContent = EMO[i % EMO.length]!;
    s.style.left = Math.random() * 100 + "%";
    s.style.animationDuration = 1.8 + Math.random() * 1.6 + "s";
    s.style.animationDelay = Math.random() * 0.5 + "s";
    s.style.fontSize = 0.8 + Math.random() * 1.2 + "rem";
    container.appendChild(s);
    setTimeout(() => s.remove(), 4000);
  }
}

/* ---------- Leaderboard (localStorage) ---------- */
function getScores(): ScoreEntry[] {
  try {
    return (JSON.parse(localStorage.getItem(LB_KEY) ?? "null") as ScoreEntry[]) || [];
  } catch (_e) {
    return [];
  }
}

export function saveScore(score: number): void {
  if (!score || score <= 0) return;
  const scores = getScores();
  scores.push({ score, date: new Date().toLocaleDateString() });
  scores.sort((a, b) => b.score - a.score);
  localStorage.setItem(LB_KEY, JSON.stringify(scores.slice(0, 10)));
}

export function renderLeaderboard(): void {
  const scores = getScores();
  const list = $("leaderboard-list");
  list.innerHTML = "";
  $("leaderboard-empty").hidden = scores.length > 0;
  scores.forEach((s) => {
    const li = document.createElement("li");
    li.innerHTML = `<span>${s.date}</span><span class="lb-score">${s.score.toLocaleString()}</span>`;
    list.appendChild(li);
  });
}

/* ---------- Learn About Waste (tap an item to see its fact) ---------- */
export function renderLearn(): void {
  const wrap = $("learn-content");
  wrap.innerHTML = "";
  BINS.forEach((bin) => {
    const group = document.createElement("div");
    group.className = "learn-group";
    group.style.background = bin.color;

    const h = document.createElement("h3");
    h.textContent = `${bin.emoji} ${bin.label}`;
    group.appendChild(h);

    const itemsWrap = document.createElement("div");
    itemsWrap.className = "learn-items";
    const factEl = document.createElement("p");
    factEl.className = "learn-fact";
    factEl.textContent = "Tap an item to learn why it goes here.";

    ITEMS_BY_BIN[bin.id].forEach((it) => {
      const chip = document.createElement("button");
      chip.className = "learn-chip";
      chip.textContent = `${it.emoji} ${it.name}`;
      chip.addEventListener("click", () => {
        factEl.textContent = `${it.emoji} ${it.fact}`;
      });
      itemsWrap.appendChild(chip);
    });

    group.appendChild(itemsWrap);
    group.appendChild(factEl);
    wrap.appendChild(group);
  });
}

/* ---------- Sound toggle ---------- */
function isSoundOn(): boolean {
  return localStorage.getItem(SOUND_KEY) !== "off";
}

export function toggleSound(): boolean {
  const on = !isSoundOn();
  localStorage.setItem(SOUND_KEY, on ? "on" : "off");
  refreshSoundLabel();
  return on;
}

export function refreshSoundLabel(): void {
  $("sound-label").textContent = isSoundOn() ? "🔊 Sound: On" : "🔇 Sound: Off";
}
